import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * SAB-979: run a callback when the app comes back to the foreground.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * A PayPal deposit and a Persona verification both finish in a system browser, and both
 * are completed SERVER-SIDE by a webhook -- the app is not involved in the money or the
 * status landing. What the app owes the user is noticing. On Android it never did: the
 * custom-scheme return was unregistered (SAB-977), so nothing told the app to re-read, and
 * even on iOS a returning user sat on a stale balance until they navigated.
 *
 * The QueryClient is configured `refetchOnWindowFocus: false` app-wide, so even the
 * surfaces that DO use react-query would not refresh on their own.
 *
 * ── WHY A CALLBACK AND NOT JUST invalidateQueries ───────────────────────────
 *
 * The wallet balance and KYC status are NOT react-query. They are useState + useEffect
 * with direct supabase.from() reads (NFLFundsSection.loadData, Wallet.tsx, WalletDeposit).
 * Invalidating the query cache would be a no-op for exactly the two surfaces this ticket
 * is about. So this hook hands the component its own re-read, and ForegroundRefetch
 * separately invalidates react-query for everything else.
 *
 * ── BOTH PLATFORMS, ONE SIGNAL ──────────────────────────────────────────────
 *
 *   native  @capacitor/app appStateChange -> isActive true
 *   web     window focus, and visibilitychange -> visible
 *
 * visibilitychange is included because a tab switched back to does not always fire focus,
 * and on mobile web returning from a payment tab is a visibility change, not a focus event.
 *
 * ── DEBOUNCED ───────────────────────────────────────────────────────────────
 *
 * Returning to a browser tab commonly fires focus AND visibilitychange together, and a
 * native resume can deliver more than one appStateChange. Each of these callbacks is a
 * database read, so they are collapsed to one per second. Not a correctness device -- a
 * double read would be harmless -- but a returning user should not cost three round trips.
 */
const MIN_GAP_MS = 1000;

export function useAppForeground(onForeground: () => void) {
  const cb = useRef(onForeground);
  cb.current = onForeground;

  useEffect(() => {
    let last = 0;
    let disposed = false;
    let removeNative: (() => void) | undefined;

    const fire = () => {
      const now = Date.now();
      if (now - last < MIN_GAP_MS) return;
      last = now;
      cb.current();
    };

    const onFocus = () => fire();
    const onVisible = () => { if (document.visibilityState === 'visible') fire(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    if (Capacitor.isNativePlatform()) {
      (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const handle = await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) fire();
          });
          if (disposed) { handle.remove(); return; }
          removeNative = () => handle.remove();
        } catch {
          // Old native binary without @capacitor/app registered. The web listeners above
          // still run inside the webview, so this degrades rather than dying.
        }
      })();
    }

    return () => {
      disposed = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      removeNative?.();
    };
  }, []);
}
